/* eslint-disable react-hooks/exhaustive-deps */
import dayjs from "dayjs";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import urlcat from "urlcat";
import { AxiosError } from "axios";
import { ROUTES } from "../../../../constants/routes";
import useHistory from "../../../../hooks/useHistory";
import { useNotifySpendingThisMonth } from "../../../../hooks/useNotifySpendingThisMonth";
import { TransactionService } from "../../../../services/transaction";
import WalletService from "../../../../services/walletService";
import { showErrorToast, showSuccessToast } from "../../../../utils/toast";
import { notifyTransactionCreated } from "../../../../utils/webViewBridge";
import { TransactionType } from "../components/types";
import { getTransactionUrl } from "../helpers";
import { TransactionFormState } from "./useTransactionFormState";
import {
  buildUnifiedPayload,
  isCreditCardInstallmentConversion,
} from "../utils/buildUnifiedPayload";

interface UseTransactionSubmitProps {
  formState: TransactionFormState;
  isDrawer: boolean;
  handleCloseDrawer?: () => void;
}

export const useTransactionSubmit = ({
  formState,
  isDrawer,
  handleCloseDrawer,
}: UseTransactionSubmitProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'transactions']);
  const { mutate } = useHistory();
  const { notifySpendingThisMonth } = useNotifySpendingThisMonth();

  const {
    transaction,
    currentTab,
    amount,
    description,
    datePicker,
    expenseWallet,
    incomeWallet,
    transferSourceWallet,
    selectDestinationWallet,
    spendingCategory,
    incomeCategory,
    loanWallet,
    loanType,
    creditCardType,
    numberOfInstallments,
    newTransactionCount,
    setNewTransactionCount,
    resetForm,
  } = formState;

  const handleTransactionSuccess = useCallback(() => {
    setNewTransactionCount(newTransactionCount + 1);
    mutate();
    resetForm();
    notifySpendingThisMonth();
    showSuccessToast(t('success.created', { ns: 'transactions' }), { hideProgressBar: true });

    if (isDrawer && handleCloseDrawer) {
      window.location.reload();
      handleCloseDrawer();
    }
  }, [newTransactionCount, mutate, t, notifySpendingThisMonth, isDrawer, handleCloseDrawer, setNewTransactionCount, resetForm]);

  const handleUpdateSuccess = useCallback(() => {
    mutate();
    notifySpendingThisMonth();
    showSuccessToast(t('success.updated', { ns: 'transactions' }), { hideProgressBar: true });
    navigate(ROUTES.HISTORY, { replace: true, state: {} });

    if (isDrawer && handleCloseDrawer) {
      window.location.reload();
      handleCloseDrawer();
    }
  }, [mutate, notifySpendingThisMonth, t, navigate, isDrawer, handleCloseDrawer]);

  /**
   * Create transaction using unified API endpoint
   * Handles all transaction types through single endpoint
   */
  const submitTransaction = useCallback(async () => {
    try {
      // Special case: Credit card installment conversion uses different API
      if (isCreditCardInstallmentConversion(currentTab, creditCardType)) {
        const installmentResponse = await WalletService.createInstallment(
          selectDestinationWallet?.wallet?.id as string,
          {
            numberOfInstallments: numberOfInstallments as number,
            conversionAmount: amount as number,
            conversionDate: dayjs(datePicker).toISOString(),
            description,
          }
        );
        handleTransactionSuccess();
        navigate(
          ROUTES.TRANSACTION_DETAIL.replace(
            ':transactionId',
            installmentResponse?.data?.conversionTransactionId || ''
          ),
          { replace: true }
        );
        return;
      }

      // Build unified payload from form state
      const payload = buildUnifiedPayload(formState);

      // Create transaction via unified endpoint
      const txn = await TransactionService.createUnifiedTransaction(payload);

      // Notify mobile app
      notifyMobileTransaction();

      // Handle success
      handleTransactionSuccess();

      // Navigate to transaction detail
      navigate(
        urlcat(
          getTransactionUrl(currentTab, loanType, expenseWallet?.wallet) || '',
          { transactionId: txn?.data?.id || '' }
        ),
        { replace: true }
      );
    } catch (axiosError: unknown) {
      handleSubmitError(axiosError);
    }
  }, [
    currentTab,
    amount,
    description,
    datePicker,
    expenseWallet,
    loanType,
    creditCardType,
    numberOfInstallments,
    selectDestinationWallet,
    formState,
    handleTransactionSuccess,
    navigate,
  ]);

  /**
   * Update transaction using unified API endpoint
   * Backend handles type changes automatically (deletes old, creates new)
   */
  const updateTransaction = useCallback(async () => {
    try {
      const transactionId = transaction?.id || '';

      // Build unified payload from form state
      const payload = buildUnifiedPayload(formState);

      // Update via unified endpoint
      await TransactionService.updateUnifiedTransaction(transactionId, payload);

      handleUpdateSuccess();
    } catch (error) {
      showErrorToast(t('error.update', { ns: 'transactions' }));
    }
  }, [transaction?.id, formState, handleUpdateSuccess, t]);

  const notifyMobileTransaction = () => {
    const getMobileTransactionType = () => {
      if (currentTab === TransactionType.EXPENSE) return 'expense';
      if (currentTab === TransactionType.INCOME) return 'income';
      if (currentTab === TransactionType.TRANSFER) return 'transfer';
      if (currentTab === TransactionType.CREDIT_CARD) return creditCardType || 'credit_card';
      if (currentTab === TransactionType.LOAN) return loanType || 'loan';
      return 'unknown';
    };

    const getWalletName = () => {
      if (currentTab === TransactionType.EXPENSE) return expenseWallet?.wallet?.name;
      if (currentTab === TransactionType.INCOME) return incomeWallet?.wallet?.name;
      if (currentTab === TransactionType.TRANSFER) return transferSourceWallet?.wallet?.name;
      if (currentTab === TransactionType.CREDIT_CARD) return transferSourceWallet?.wallet?.name;
      if (currentTab === TransactionType.LOAN) return loanWallet?.wallet?.name;
      return undefined;
    };

    const getCategoryName = () => {
      if (currentTab === TransactionType.EXPENSE) return spendingCategory?.name;
      if (currentTab === TransactionType.INCOME) return incomeCategory?.name;
      return undefined;
    };

    notifyTransactionCreated({
      transactionType: getMobileTransactionType(),
      amount: amount || 0,
      walletName: getWalletName(),
      categoryName: getCategoryName(),
      description: description || undefined,
      transactionDate: dayjs(datePicker).toISOString(),
    });
  };

  const handleSubmitError = (axiosError: unknown) => {
    const error = (axiosError as AxiosError<any>)?.response?.data?.error;
    let msg = error?.message;
    if (loanType === 'collect') {
      msg = 'Collection amount exceeds loan balance';
    }
    if (msg) {
      showErrorToast(t(`error.${msg}`, { ns: 'transactions' }));
    } else {
      showErrorToast(t('error.create', { ns: 'transactions' }));
    }
  };

  return {
    submitTransaction,
    updateTransaction,
    handleTransactionSuccess,
    handleUpdateSuccess,
  };
};
